import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import TopBar from "../Structural/TopBar";
import SecondaryTopBar from "../Structural/SecondaryTopBar";
import Sidebar from "../Structural/Sidebar";
import ContentRouting from "../Structural/ContentRouting";
import Footer from "../Structural/Footer";
import BottomNav from "../Structural/BottomNav";
import MobileTopBar from "../Structural/MobileTopBar";
import PromoBar from "../Structural/PromoBar";
import routesConfig from "../../Config/sidebarConfig";
import NewReservation from "../../Pages/NieuweReservatie/Rendering";
import FullScreenNotifications from "../FullScreenNotifications";
import TodayNotifications from "../TodayNotifications";
import { FiBell } from "react-icons/fi";
import "./css/mainApp.css";
import useApi from "../../Hooks/useApi";
import useSessionTracker from "../../Hooks/useSessionTracker";
import sound1 from "../../Assets/sounds/sound1.mp3";
import HolidayOverlay from "../HolidayOverlay/HolidayOverlay";
import IncidentStatusBanner from "../IncidentStatusBanner";
import PaymentWarningBanner from "../PaymentWarningBanner";
import AccountFrozenOverlay from "../AccountFrozenOverlay";
import FullScreenOnboarding from "../Onboarding/FullScreenOnboarding";
import SetupProgressCard from "../SetupProgressCard/SetupProgressCard";
import { BlockedSlotsManager } from "@happychef/blocked-slots";
import useTranslations from "../../Hooks/useTranslations";
import translations from "./translations.json";

// Tutorial system
import Tutorial, { TutorialProvider, useTutorial } from "../Tutorial";

import TermsConsentModal from "../TermsConsentModal/index";
import FeedbackModal from "../FeedbackModal";

// Pull account from RTK Query store + mutation for saving
import {
  useGetAccountQuery,
  useUpdateAccountMutation,
} from "../../store/accountApi";

import {
  useGetRestaurantSetupQuery,
} from "../../store/setupApi";

// Only show FeedbackModal at least 1 day after tutorial completion
function FeedbackGate({ show, children }) {
  const { tutorialCompleted, tutorialCompletedAt } = useTutorial();
  // Hide feedback entirely for admin visits (_admin login suffix)
  if (localStorage.getItem('adminVisit') === 'true') return null;
  if (!show || !tutorialCompleted) return null;
  if (tutorialCompletedAt) {
    const oneDayMs = 24 * 60 * 60 * 1000;
    if (Date.now() - new Date(tutorialCompletedAt).getTime() < oneDayMs) return null;
  }
  return children;
}

const TERMS_VERSION = "v2025-10-14";
const TERMS_URL = "https://voorwaarden.happychef.be/";

const MainApp = () => {
  useSessionTracker();
  const t = useTranslations(translations);
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const [isSidebarExpanded, setSidebarExpanded] = useState(
    window.innerWidth >= 900
  );
  const [isNotificationsOpen, setNotificationsOpen] = useState(false);
  const [isTodayNotificationsVisible, setTodayNotificationsVisible] =
    useState(true);
  const [todayNotificationsDismissed, setTodayNotificationsDismissed] =
    useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [todayChangesCount, setTodayChangesCount] = useState(0);
  const [lastNotificationTimestamp, setLastNotificationTimestamp] =
    useState(null);

  const api = useApi();

  // Restaurant setup for blocked slots manager
  const {
    data: restaurantSetupData,
    isLoading: isLoadingSetup,
    refetch: refetchSetup,
  } = useGetRestaurantSetupQuery();

  const currentRoute = routesConfig.find(
    (route) => route.path === location.pathname
  );
  const isSidebarHidden =
    currentRoute && currentRoute.sidebarHidden ? true : false;
  const showSecondaryTopBar = !(
    location.pathname === "/" && query.has("preview")
  );

  // ===== Onboarding state (uses store data) =====
  const {
    data: account,
    isLoading: accountLoading,
    isError: accountError,
  } = useGetAccountQuery(undefined, { refetchOnMountOrArgChange: false });

  const [updateAccount, { isLoading: isUpdating }] = useUpdateAccountMutation();

  const [requireOnboarding, setRequireOnboarding] = useState(false);
  const [onbSaving, setOnbSaving] = useState(false);
  const [onbError, setOnbError] = useState("");

  const [mustAcceptTerms, setMustAcceptTerms] = useState(false);

  const isProfileIncomplete = (p) => {
    const email = p?.email;
    const restaurantName =
      p?.restaurant_name ||
      p?.restaurantName ||
      p?.companyName ||
      p?.businessName ||
      p?.tenantName;
    const emailOk = typeof email === "string" && /\S+@\S+\.\S+/.test(email);
    const restaurantOk =
      typeof restaurantName === "string" && restaurantName.trim().length > 1;
    return !(emailOk && restaurantOk);
  };

  // Decide whether to gate with onboarding when account loads
  useEffect(() => {
    if (account && !accountError) {
      setRequireOnboarding(isProfileIncomplete(account));
    }
  }, [account, accountError]);

  useEffect(() => {
    if (!account || accountError || accountLoading) return;
    if (requireOnboarding) {
      // handle onboarding first; terms will be checked again after onboarding save
      setMustAcceptTerms(false);
      return;
    }
    // Skip terms for admin visits (flagged via _admin login suffix)
    if (localStorage.getItem("adminVisit") === "true") {
      setMustAcceptTerms(false);
      return;
    }
    const acceptedAt = account?.termsAcceptedAt;
    const version = account?.termsVersion;
    const needsPrompt = !(acceptedAt && version === TERMS_VERSION);
    setMustAcceptTerms(needsPrompt);
  }, [account, accountError, accountLoading, requireOnboarding]);

  const handleOnboardingSave = async ({ email, restaurantName }) => {
    setOnbError("");
    setOnbSaving(true);
    const payload = { email, restaurant_name: restaurantName };

    try {
      await updateAccount(payload).unwrap();
      setRequireOnboarding(false);
      if (restaurantName)
        localStorage.setItem("restaurantName", restaurantName);
    } catch (rtkErr) {
      console.error(
        "[Onboarding] RTKQ updateAccount failed, fallback:",
        rtkErr
      );
      try {
        await api.patch(`${window.baseDomain}api/account/`, payload);
        setRequireOnboarding(false);
        if (restaurantName)
          localStorage.setItem("restaurantName", restaurantName);
      } catch (fallbackErr) {
        console.error("[Onboarding] Fallback PATCH failed:", fallbackErr);
        setOnbError(t.onboarding.saveError);
      }
    } finally {
      setOnbSaving(false);
    }
  };

  const handleTermsAgree = async () => {
    try {
      await updateAccount({
        termsAcceptedAt: new Date().toISOString(),
        termsVersion: TERMS_VERSION,
        termsUrl: TERMS_URL,
      }).unwrap();
      setMustAcceptTerms(false);
    } catch (e) {
      console.error("[Terms] Failed to save acceptance", e);
      // Fallback: try legacy PATCH if present in your API
      try {
        await api.patch(`${window.baseDomain}api/account/`, {
          termsAcceptedAt: new Date().toISOString(),
          termsVersion: TERMS_VERSION,
          termsUrl: TERMS_URL,
        });
        setMustAcceptTerms(false);
      } catch (fallbackErr) {
        console.error("[Terms] Fallback PATCH failed:", fallbackErr);
        alert(t.terms.saveError);
      }
    }
  };

  const handleTermsDisagree = async () => {
    localStorage.setItem("loginSuccessful", false);
    localStorage.removeItem("restaurantName");
    window.location.reload();
  };

  // ===== Notifications system (unchanged) =====
  const handleCloseTodayNotifications = () => {
    setTodayNotificationsVisible(false);
    setTodayNotificationsDismissed(true);
  };

  const isToday = (dateString) => {
    const today = new Date();
    const todayStr = today.toISOString().split("T")[0];
    return dateString === todayStr;
  };

  const isTimestampToday = (timestamp) => {
    const today = new Date();
    const timestampDate = new Date(timestamp);
    return today.toDateString() === timestampDate.toDateString();
  };

  const shouldShowNotification = (notification) => {
    if (!notification.date || !notification.time) return true;
    const today = new Date();
    const todayStr = today.toISOString().split("T")[0];
    if (notification.date !== todayStr) return true;
    const [hours, minutes] = notification.time.split(":").map(Number);
    const notificationTime = new Date();
    notificationTime.setHours(hours, minutes, 0, 0);
    const fiveMinutesAgo = new Date(today.getTime() - 5 * 60 * 1000);
    return notificationTime > fiveMinutesAgo;
  };

  const calculateTodayChanges = (notifications) => {
    if (!notifications || notifications.length === 0)
      return { total: 0, unseen: 0 };
    const seenNotifications = JSON.parse(
      localStorage.getItem("seenTodayNotifications") || "[]"
    );
    const todayNotifications = notifications.filter((notification) => {
      if (notification.event === "giftcard_purchase") return false;
      const hasDateToday = notification.date && isToday(notification.date);
      if (!hasDateToday) return false;
      const hasTimestampToday =
        notification.creationTimestamp &&
        isTimestampToday(notification.creationTimestamp);
      if (!hasTimestampToday) return false;
      const isNotPast = shouldShowNotification(notification);
      if (!isNotPast) return false;
      return true;
    });
    const unseenCount = todayNotifications.filter(
      (n) => !seenNotifications.includes(n._id || n.reservationId)
    ).length;

    return { total: todayNotifications.length, unseen: unseenCount };
  };

  // Fetch notifications once on mount only
  useEffect(() => {
    const fetchInitialNotifications = async () => {
      try {
        const notifications = await api.get(
          `${window.baseDomain}api/notifications`,
          { noCache: true }
        );

        if (notifications && notifications.length > 0) {
          const currentTimestamp = Date.now().toString();
          localStorage.setItem(
            "notificationsCache",
            JSON.stringify(notifications)
          );
          localStorage.setItem("notificationsCacheTimestamp", currentTimestamp);

          const { unseen } = calculateTodayChanges(notifications);
          setTodayChangesCount(unseen);

          const unreadNotifications = (notifications || []).filter(
            (n) => !n.read
          );
          setUnreadCount(unreadNotifications.length);
        }
      } catch (error) {
        console.error("Error fetching initial notifications:", error);
      }
    };

    fetchInitialNotifications();
  }, [api]); // Only run once on mount

  // Listen for Socket.IO notification events for real-time updates via Redux
  useEffect(() => {
    const handleNotificationCreated = async (notification) => {
      console.log("🔔 [MainApp] New notification received via Redux", notification);

      // Get current notifications from cache
      const cached = localStorage.getItem("notificationsCache");
      const notifications = cached ? JSON.parse(cached) : [];

      // Add new notification to the beginning of the array
      const updatedNotifications = [notification, ...notifications];

      // Update cache
      const currentTimestamp = Date.now().toString();
      localStorage.setItem(
        "notificationsCache",
        JSON.stringify(updatedNotifications)
      );
      localStorage.setItem("notificationsCacheTimestamp", currentTimestamp);

      // Update today changes count
      const { unseen } = calculateTodayChanges(updatedNotifications);
      setTodayChangesCount(unseen);

      // Update unread count
      const unreadNotifications = updatedNotifications.filter((n) => !n.read);
      setUnreadCount(unreadNotifications.length);

      // Check if we should show today notifications popup
      const shouldShowTodayNotifications = () => {
        if (unseen === 0) return false;
        const validNotifications = updatedNotifications.filter((n) => {
          if (n.event === "giftcard_purchase") return false;
          if (!n.date || !isToday(n.date)) return false;
          if (!n.creationTimestamp || !isTimestampToday(n.creationTimestamp))
            return false;
          if (!shouldShowNotification(n)) return false;
          const seenNotifications = JSON.parse(
            localStorage.getItem("seenTodayNotifications") || "[]"
          );
          return !seenNotifications.includes(n._id || n.reservationId);
        });

        return validNotifications.length > 0;
      };

      if (shouldShowTodayNotifications()) {
        setTodayNotificationsVisible(true);
        if (todayNotificationsDismissed) setTodayNotificationsDismissed(false);
      }

      // Play sound if enabled
      const storedSoundSettings = localStorage.getItem("soundSettings");
      const soundSettings = storedSoundSettings
        ? JSON.parse(storedSoundSettings)
        : {
            geluidNieuweReservatie: "Geluid Aanzetten",
            geluidUpdateReservatie: "Geen Geluid",
            geluidVerwijderdeReservatie: "Geluid Aanzetten",
          };

      const shouldPlaySound =
        (notification.event === "creation" &&
          soundSettings.geluidNieuweReservatie === "Geluid Aanzetten") ||
        (notification.event === "update" &&
          soundSettings.geluidUpdateReservatie === "Geluid Aanzetten") ||
        (notification.event === "deletion" &&
          soundSettings.geluidVerwijderdeReservatie === "Geluid Aanzetten");

      if (shouldPlaySound && notification.reservationType !== "auth" && !notification.soundPlayed) {
        const audio = new Audio(sound1);
        try {
          await audio.play();
        } catch (playError) {
          if (playError.name === 'NotAllowedError') {
            console.log('Audio playback blocked - user interaction required first');
          } else {
            console.error('Error playing notification sound:', playError);
          }
        }

        // Mark sound as played
        try {
          await api.patch(`${window.baseDomain}api/notifications/sound`, {
            notificationIds: [notification._id || notification.reservationId],
          });
        } catch (soundErr) {
          console.error("Error marking sound as played:", soundErr);
        }
      }
    };

    // Listen for notification events from socketMiddleware via custom event
    const handleReduxNotification = (event) => {
      handleNotificationCreated(event.detail);
    };

    // Use custom event as bridge between middleware and component
    window.addEventListener("redux:notifications/created", handleReduxNotification);

    return () => {
      window.removeEventListener("redux:notifications/created", handleReduxNotification);
    };
  }, [api, todayNotificationsDismissed]);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (
        e.key === "notificationsCache" ||
        e.key === "seenTodayNotifications"
      ) {
        const cached = localStorage.getItem("notificationsCache");
        if (cached) {
          const notifications = JSON.parse(cached);
          const { unseen } = calculateTodayChanges(notifications);
          setTodayChangesCount(unseen);

          if (unseen === 0) {
            setTodayNotificationsVisible(false);
            setTodayNotificationsDismissed(false);
          } else {
            if (!todayNotificationsDismissed) {
              setTodayNotificationsVisible(true);
            }
          }
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [todayNotificationsDismissed]);

  // ======= BLOCK APP UNTIL ONBOARDING DONE =======
  if (!accountLoading && !accountError && requireOnboarding) {
    return (
      <FullScreenOnboarding
        initialEmail={account?.email || ""}
        initialRestaurantName={
          account?.restaurant_name ||
          account?.restaurantName ||
          account?.companyName ||
          account?.businessName ||
          account?.tenantName ||
          ""
        }
        saving={onbSaving || isUpdating}
        error={onbError}
        onSave={handleOnboardingSave}
      />
    );
  }

  // ======= BLOCK APP IF ACCOUNT FROZEN =======
  const paymentStatus = account?.paymentStatus || "";
  const isAdminVisit = localStorage.getItem("adminVisit") === "true";

  if (!accountLoading && !accountError && paymentStatus === "frozen" && !isAdminVisit) {
    return <AccountFrozenOverlay />;
  }

  // ======= Normal app =======
  return (
    <TutorialProvider account={account} updateAccount={updateAccount}>
    <div
      className={`app-component ${isSidebarHidden ? "sidebar-hidden" : ""} ${
        !isSidebarHidden && isSidebarExpanded ? "sidebar-expanded" : ""
      }`}
    >
      <PromoBar />
      <BlockedSlotsManager
        restaurantSetup={restaurantSetupData}
        isLoadingSetup={isLoadingSetup}
        onSetupRefetch={async () => {
          const res = refetchSetup();
          if (res?.unwrap) {
            try { await res.unwrap(); } catch (err) { console.error(err); }
          }
        }}
      />
      <NewReservation />
      <TopBar />
      <MobileTopBar
        onNotifications={() => {
          setUnreadCount(0);
          setNotificationsOpen(true);
        }}
        unreadCount={unreadCount}
      />
      <IncidentStatusBanner />
      <PaymentWarningBanner paymentStatus={paymentStatus} />

      <div className="app-layout">
        {!isSidebarHidden && (
          <Sidebar
            onToggleExpand={(expanded) => setSidebarExpanded(expanded)}
          />
        )}

        <div className="content-area">
          <main className="page-container">
            <SetupProgressCard
              autoHideWhenComplete={true}
              onComplete={() => {
                console.log("Setup complete 🎉");
              }}
            />
            {showSecondaryTopBar && <SecondaryTopBar />}
            <ContentRouting />
          </main>
          <Footer isSidebarExpanded={isSidebarExpanded} />
        </div>
      </div>

      <FullScreenNotifications
        isOpen={isNotificationsOpen}
        onClose={() => setNotificationsOpen(false)}
      />
      <TodayNotifications
        isVisible={isTodayNotificationsVisible && todayChangesCount > 0}
        onClose={handleCloseTodayNotifications}
      />
      <button
        className="floating-notification-button"
        onClick={() => {
          setUnreadCount(0);
          setNotificationsOpen(true);
        }}
      >
        <FiBell size={24} color="var(--color-yellow)" />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? "+99" : unreadCount}
          </span>
        )}
      </button>

      {/* Mobile bottom navigation */}
      {!isSidebarHidden && <BottomNav />}

      {/* ⬇⬇⬇ ADD: Blocking Terms modal (shown only when needed) */}
      <TermsConsentModal
        open={!accountLoading && !accountError && mustAcceptTerms}
        onAgree={handleTermsAgree}
        onDisagree={handleTermsDisagree}
        title={t.terms.title}
        termsUrl={TERMS_URL}
        agreeLabel={t.terms.agreeLabel}
        disagreeLabel={t.terms.disagreeLabel}
      />

      {/* Feedback/satisfaction modal — only after tutorial completed & terms accepted */}
      <FeedbackGate show={!mustAcceptTerms && !accountLoading && !accountError}>
        <FeedbackModal />
      </FeedbackGate>

      {/* Tutorial system */}
      <Tutorial />
    </div>
    </TutorialProvider>
  );
};

export default MainApp;
